import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { TransactionTeamLeadParams, TransactionTeamTransactionRoleParams } from '../../models/APIModels';
import {
  AimPropertySummary,
  AimPropertySummaryChangelog,
  TransactionTeam
} from '../../models/TransactionTeamModels';
import { transactionTeamEndpoints } from '../endpoints/transactionTeamEndpoints';
import { TransactionRole } from '../../models/AIMModels';
import { AIM_SERVICE } from '../../envUtils';

const AimApiQueryCreator = () => {
  let getToken: () => Promise<string> | string = () => '';
  const setTokenGetter = (getter: () => Promise<string> | string) => {
    getToken = getter;
  };
  const api = createApi({
    reducerPath: 'transactionTeamsApi',
    baseQuery: fetchBaseQuery({
      baseUrl: `${AIM_SERVICE}`,
      prepareHeaders: async (headers) => {
        const token = await getToken();
        headers.set('Authorization', `Bearer ${token}`);
        return headers;
      }
    }),
    tagTypes: ['TransactionTeam', 'TransactionRole', 'PropertySummary'],
    endpoints: (builder) => ({
      getTransactionTeams: builder.query<TransactionTeam[], null>({
        query: () => transactionTeamEndpoints.transactionTeams,
        providesTags: ['TransactionTeam']
      }),
      getTransactionTeam: builder.query<TransactionTeam, Pick<TransactionTeam, 'aimTransactionTeam'>>({
        query: (aimTransactionTeam) => transactionTeamEndpoints.transactionTeamDetails(aimTransactionTeam),
        //@ts-ignore
        transformResponse: (baseQueryReturnValue: TransactionTeam[], meta, arg) => {
          return baseQueryReturnValue[0];
        },
        providesTags: ['TransactionTeam']
      }),
      newTransactionTeam: builder.mutation<TransactionTeam[], TransactionTeam>({
        query: ({ ...payload }) => ({
          url: transactionTeamEndpoints.newTransactionTeam,
          method: 'POST',
          formData: payload
        }),
        invalidatesTags: ['TransactionTeam']
      }),
      getTransactionRoles: builder.query<TransactionRole[], null>({
        query: () => transactionTeamEndpoints.transactionRoles,
        providesTags: ['TransactionRole']
      }),
      getTransactionRole: builder.query<TransactionRole[], Pick<TransactionRole, 'aimTransactionRole'>>({
        query: (aimTransactionRole) => transactionTeamEndpoints.transactionRoleDetails(aimTransactionRole),
        providesTags: ['TransactionRole']
      }),
      updateTransactionTeam: builder.mutation<TransactionTeam[], TransactionTeam>({
        query: ({ ...payload }) => ({
          url: transactionTeamEndpoints.updateTransactionTeam(payload.aimTransactionTeam),
          method: 'POST',
          formData: payload
        }),
        invalidatesTags: ['TransactionTeam']
      }),
      deleteTransactionTeam: builder.mutation<TransactionTeam[], Pick<TransactionTeam, 'aimTransactionTeam'>>(
        {
          query: (aimTransactionTeam) => ({
            url: transactionTeamEndpoints.deleteTransactionTeam(aimTransactionTeam),
            method: 'POST'
          }),
          invalidatesTags: ['TransactionTeam']
        }
      ),
      cancelTransactionTeam: builder.mutation<
        TransactionTeam,
        { aimTransactionTeam: Pick<TransactionTeam, 'aimTransactionTeam'>; data: FormData }
      >({
        query: ({ ...payload }) => ({
          url: transactionTeamEndpoints.cancelTransactionTeam(payload.aimTransactionTeam),
          method: 'POST',
          body: payload.data
        }),
        invalidatesTags: ['TransactionTeam']
      }),
      submitTransactionTeam: builder.mutation<TransactionTeam[], Pick<TransactionTeam, 'aimTransactionTeam'>>(
        {
          query: (aimTransactionTeam) => ({
            url: transactionTeamEndpoints.submitTransactionTeam(aimTransactionTeam),
            method: 'POST'
          }),
          invalidatesTags: ['TransactionTeam']
        }
      ),
      revertTransactionTeam: builder.mutation<TransactionTeam[], Pick<TransactionTeam, 'aimTransactionTeam'>>(
        {
          query: (aimTransactionTeam) => ({
            url: transactionTeamEndpoints.revertTransactionTeam(aimTransactionTeam),
            method: 'POST'
          }),
          invalidatesTags: ['TransactionTeam']
        }
      ),
      formTransactionTeam: builder.mutation<TransactionTeam[], Pick<TransactionTeam, 'aimTransactionTeam'>>({
        query: (aimTransactionTeam) => ({
          url: transactionTeamEndpoints.formTransactionTeam(aimTransactionTeam),
          method: 'POST'
        }),
        invalidatesTags: ['TransactionTeam']
      }),
      archiveTransactionTeam: builder.mutation<
        TransactionTeam[],
        Pick<TransactionTeam, 'aimTransactionTeam'>
      >({
        query: (aimTransactionTeam) => ({
          url: transactionTeamEndpoints.archiveTransactionTeam(aimTransactionTeam),
          method: 'POST'
        }),
        invalidatesTags: ['TransactionTeam']
      }),
      proposeTransactionTeamLead: builder.mutation<TransactionTeam[], TransactionTeamLeadParams>({
        query: ({ ...payload }) => ({
          url: transactionTeamEndpoints.proposeTransactionTeamLead(payload),
          method: 'POST',
          formData: { aimEmployee: payload.aimEmployee }
        }),
        invalidatesTags: ['TransactionTeam']
      }),
      approveTransactionTeamLead: builder.mutation<TransactionTeam[], TransactionTeamTransactionRoleParams>({
        query: ({ ...payload }) => ({
          url: transactionTeamEndpoints.approveTransactionTeamLead(payload),
          method: 'POST'
        }),
        invalidatesTags: ['TransactionTeam']
      }),
      replaceTransactionTeamLead: builder.mutation<TransactionTeam, TransactionTeamLeadParams>({
        query: ({ ...payload }) => ({
          url: transactionTeamEndpoints.replaceTransactionTeamLead(payload),
          method: 'POST',
          body: payload.aimEmployee
        }),
        invalidatesTags: ['TransactionTeam']
      }),
      deleteTransactionTeamLead: builder.mutation<TransactionTeam[], TransactionTeamTransactionRoleParams>({
        query: ({ ...payload }) => ({
          url: transactionTeamEndpoints.deleteTransactionTeamLead(payload),
          method: 'POST'
        }),
        invalidatesTags: ['TransactionTeam']
      }),
      raiseTransactionTeamPriority: builder.mutation<
        TransactionTeam,
        Pick<TransactionTeam, 'aimTransactionTeam'>
      >({
        query: (aimTransactionTeam) => ({
          url: transactionTeamEndpoints.raiseTransactionTeamPriority(aimTransactionTeam),
          method: 'GET'
        }),
        invalidatesTags: ['TransactionTeam']
      }),
      lowerTransactionTeamPriority: builder.mutation<
        TransactionTeam,
        Pick<TransactionTeam, 'aimTransactionTeam'>
      >({
        query: (aimTransactionTeam) => ({
          url: transactionTeamEndpoints.lowerTransactionTeamPriority(aimTransactionTeam),
          method: 'GET'
        }),
        invalidatesTags: ['TransactionTeam']
      }),
      addJarvisData: builder.mutation<TransactionTeam, { aimTransactionTeam: number; data: FormData }>({
        query: ({ ...payload }) => ({
          url: transactionTeamEndpoints.addJarvisData(payload.aimTransactionTeam as number),
          method: 'POST',
          body: payload.data
        }),
        invalidatesTags: ['TransactionTeam']
      }),
      changeMilestone: builder.mutation<TransactionTeam, { aimTransactionTeam: number; data: FormData }>({
        query: ({ aimTransactionTeam, data }) => ({
          url: transactionTeamEndpoints.changeMilestone(aimTransactionTeam),
          method: 'POST',
          body: data
        }),
        invalidatesTags: ['TransactionTeam']
      }),
      rejectTransaction: builder.mutation<TransactionTeam, { aimTransactionTeam: number; reason: FormData }>({
        query: ({ aimTransactionTeam, reason }) => ({
          url: transactionTeamEndpoints.rejectTransaction(aimTransactionTeam),
          method: 'POST',
          body: reason
        }),
        invalidatesTags: ['TransactionTeam']
      }),
      failTransaction: builder.mutation<TransactionTeam, { aimTransactionTeam: number; reason: FormData }>({
        query: ({ aimTransactionTeam, reason }) => ({
          url: transactionTeamEndpoints.failTransaction(aimTransactionTeam),
          method: 'POST',
          body: reason
        }),
        invalidatesTags: ['TransactionTeam']
      }),
      propertySummary: builder.query<AimPropertySummary[], number>({
        query: (aimTransactionTeam) => ({
          url: transactionTeamEndpoints.getPropertySummary(aimTransactionTeam),
          method: 'GET'
        }),
        providesTags: ['PropertySummary']
      }),
      addPropertySummary: builder.mutation<
        AimPropertySummary[],
        {
          aimTransactionTeam: number;
          key: keyof AimPropertySummary;
          data: FormData;
        }
      >({
        query: ({ aimTransactionTeam, data, key }) => ({
          url: transactionTeamEndpoints.addPropertySummaryItem(aimTransactionTeam, key),
          method: 'POST',
          body: data
        }),
        invalidatesTags: ['PropertySummary']
      }),
      propertySummaryChangeLog: builder.query<AimPropertySummaryChangelog[], number>({
        query: (aimTransactionTeam) => ({
          url: transactionTeamEndpoints.propertySummaryChangeLog(aimTransactionTeam),
          method: 'GET'
        }),
        providesTags: ['PropertySummary']
      }),
      editTransactionTitle: builder.mutation<
          TransactionTeam,
          { aimTransactionTeam: number; title: FormData }
          >({
        query: ({ aimTransactionTeam, title }) => ({
          url: transactionTeamEndpoints.editTransactionTitle(aimTransactionTeam),
          method: 'POST',
          body: title
        }),
        invalidatesTags: ['TransactionTeam']
      }),
      editClosingDate: builder.mutation<
        TransactionTeam,
        { aimTransactionTeam: number; closingDate: FormData }
      >({
        query: ({ aimTransactionTeam, closingDate }) => ({
          url: transactionTeamEndpoints.editClosingDate(aimTransactionTeam),
          method: 'POST',
          body: closingDate
        }),
        invalidatesTags: ['TransactionTeam']
      }),
      editClosingAcreage: builder.mutation<
        TransactionTeam,
        { aimTransactionTeam: number; closingAcreage: FormData }
      >({
        query: ({ aimTransactionTeam, closingAcreage }) => ({
          url: transactionTeamEndpoints.editClosingAcreage(aimTransactionTeam),
          method: 'POST',
          body: closingAcreage
        }),
        invalidatesTags: ['TransactionTeam']
      }),
      editClosingPrice: builder.mutation<
        TransactionTeam,
        { aimTransactionTeam: number; closingPrice: FormData }
      >({
        query: ({ aimTransactionTeam, closingPrice }) => ({
          url: transactionTeamEndpoints.editClosingPrice(aimTransactionTeam),
          method: 'POST',
          body: closingPrice
        }),
        invalidatesTags: ['TransactionTeam']
      }),
    })
  });
  return { hooks: api, setTokenGetter };
};

/**
 * An RTK Query service providing React hooks for transaction team management endpoints in the AIM platform, built with `fetchBaseQuery` and automatic Bearer-token injection.
 */
export const TransactionTeamService = AimApiQueryCreator();
export const {
  useGetTransactionRolesQuery,
  useGetTransactionTeamsQuery,
  useGetTransactionTeamQuery,
  useLazyGetTransactionTeamQuery,
  useRaiseTransactionTeamPriorityMutation,
  useLowerTransactionTeamPriorityMutation,
  useCancelTransactionTeamMutation,
  useProposeTransactionTeamLeadMutation,
  useApproveTransactionTeamLeadMutation,
  useReplaceTransactionTeamLeadMutation,
  useDeleteTransactionTeamLeadMutation,
  useRejectTransactionMutation,
  useFailTransactionMutation,
  useAddJarvisDataMutation,
  useChangeMilestoneMutation,
  usePropertySummaryQuery,
  useAddPropertySummaryMutation,
  usePropertySummaryChangeLogQuery,
  useEditTransactionTitleMutation,
  useEditClosingDateMutation,
  useEditClosingAcreageMutation,
  useEditClosingPriceMutation
} = TransactionTeamService.hooks;
